// src/screens/admin/contact/AdminContactMessages.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Container } from 'react-bootstrap';
import {
  FaSearch,
  FaPaperPlane,
  FaEnvelope,
  FaEnvelopeOpen,
  FaPhone,
  FaCheckCircle,
} from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useSearchParams } from 'react-router-dom';
import '../../../assets/css/admin/messages/AdminMessages.css';
import AdminLayout from '../../../layouts/AdminLayout';
import MyContext from '../../../context/MyContext';
import { MessagesPageSkeleton } from '../../../components/general/skeleton/SkeletonBlock';
import getEcho from '../../../components/general/echo/Echo';

const AdminMessages = () => {
  const { baseUrl, authToken, loggedInAdmin } = useContext(MyContext);
  const [searchParams] = useSearchParams();

  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // ════════════════════════════════
  // API: viewAllMessages
  // ════════════════════════════════
  const viewAllMessages = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/contact/viewAllMessages`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then(({ data }) => {
        setLoading(false);
        if (data.success) {
          setMessages(data.messages);
        } else {
          Swal.fire('Error!', data.message, 'error');
        }
      })
      .catch(() => {
        setLoading(false);
        Swal.fire('Error!', 'Failed to load messages.', 'error');
      });
  };

  useEffect(() => {
    if (!authToken) return;
    viewAllMessages();
  }, [authToken]);

  // ── Pusher — naya contact-form message aate hi list turant refresh ho,
  //    bina page reload kiye ──
  useEffect(() => {
    if (!authToken || !loggedInAdmin?.id) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channel = echo.private(`admin.${loggedInAdmin.id}`);
    channel.listen('.new-notification', (data) => {
      if (data.notification?.for === 'contact') {
        viewAllMessages();
      }
    });

    return () => {
      echo.leave(`admin.${loggedInAdmin.id}`);
    };
  }, [authToken, loggedInAdmin?.id, baseUrl]);

  // Auto-open the message linked from Topbar (?contact=ID)
  useEffect(() => {
    const contactId = searchParams.get('contact');
    if (!contactId || messages.length === 0) return;

    const match = messages.find((m) => String(m.id) === contactId);
    if (match) {
      setSelectedMessage(match);
      setReplyText('');
    }
  }, [messages, searchParams]);

  // ════════════════════════════════
  // API: sendReply
  // ════════════════════════════════
  const sendReply = () => {
    if (!replyText.trim() || !selectedMessage) return;

    setSending(true);
    const fd = new FormData();
    fd.append('id', selectedMessage.id);
    fd.append('reply', replyText);

    axios
      .post(`${baseUrl}/admin/contact/sendReply`, fd, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then(({ data }) => {
        setSending(false);
        if (data.success) {
          Swal.fire('Sent!', 'Reply emailed to the customer.', 'success');
          setReplyText('');
          viewAllMessages();
          setSelectedMessage({ ...selectedMessage, reply: replyText, replied_at: new Date().toISOString() });
        } else {
          Swal.fire('Error!', data.message, 'error');
        }
      })
      .catch(() => {
        setSending(false);
        Swal.fire('Error!', 'Failed to send reply.', 'error');
      });
  };

  const filteredMessages = messages.filter((m) =>
    m.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    m.subject?.toLowerCase().includes(searchText.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchText.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ── Loading ───────────────────────────────────────────────────
  if (loading)
    return (
      <AdminLayout>
        <MessagesPageSkeleton />
      </AdminLayout>
    );

  return (
    <AdminLayout>
      <div className="adm-msg-container">
        <Container fluid>
          <div className="adm-msg-wrapper">

            {/* Sidebar - Message List */}
            <div className="adm-msg-sidebar">
              <div className="adm-msg-sidebar-header">
                <h3>Contact Messages</h3>
              </div>

              <div className="adm-msg-search">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search by name, email, subject..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>

              <div className="adm-msg-chat-list">
                {!loading && filteredMessages.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>No messages found.</div>
                )}

                {!loading && filteredMessages.map((m) => (
                  <div
                    key={m.id}
                    className={`adm-msg-chat-item ${selectedMessage?.id === m.id ? 'active' : ''}`}
                    onClick={() => { setSelectedMessage(m); setReplyText(''); }}
                  >
                    <div className="adm-msg-chat-avatar">
                      {m.reply ? <FaEnvelopeOpen size={18} /> : <FaEnvelope size={18} />}
                    </div>
                    <div className="adm-msg-chat-info">
                      <div className="adm-msg-chat-header">
                        <h6>{m.name}</h6>
                        <span className="adm-msg-chat-time">{formatDate(m.created_at)}</span>
                      </div>
                      <div className="adm-msg-chat-preview">
                        <p>{m.subject}</p>
                        {!m.reply && <span className="adm-msg-unread-badge">New</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Detail Area */}
            <div className="adm-msg-chat-area">
              {selectedMessage ? (
                <>
                  <div className="adm-msg-chat-header">
                    <div className="adm-msg-header-left">
                      <div className="adm-msg-header-info">
                        <h5>{selectedMessage.name}</h5>
                        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                          {selectedMessage.email}
                          {selectedMessage.phone && (
                            <> &nbsp;·&nbsp; <FaPhone size={11} /> {selectedMessage.phone}</>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="adm-msg-messages-area">
                    <div className="adm-msg-messages-wrapper">
                      <div className="adm-msg-message received">
                        <div className="adm-msg-bubble">
                          <p style={{ fontWeight: 700, marginBottom: 4 }}>{selectedMessage.subject}</p>
                          <p>{selectedMessage.message}</p>
                          <div className="adm-msg-meta">
                            <span className="adm-msg-time">{formatDate(selectedMessage.created_at)}</span>
                          </div>
                        </div>
                      </div>

                      {selectedMessage.reply && (
                        <div className="adm-msg-message sent">
                          <div className="adm-msg-bubble">
                            <p>{selectedMessage.reply}</p>
                            <div className="adm-msg-meta">
                              <FaCheckCircle size={11} />
                              <span className="adm-msg-time">Emailed {formatDate(selectedMessage.replied_at)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="adm-msg-input-area">
                    <div className="adm-msg-input-wrapper">
                      <input
                        type="text"
                        placeholder={selectedMessage.reply ? 'Send another reply...' : 'Type your reply — sent to their email...'}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') sendReply();
                        }}
                      />
                    </div>
                    <button
                      className="adm-msg-send-btn"
                      onClick={() => sendReply()}
                      disabled={!replyText.trim() || sending}
                    >
                      <FaPaperPlane />
                    </button>
                  </div>
                </>
              ) : (
                <div className="adm-msg-empty-state">
                  <div className="adm-msg-empty-icon">📩</div>
                  <h3>Select a message</h3>
                  <p>Choose a contact submission from the list to view and reply</p>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
